/**
 * Central country/locale config — frontend mirror of backend COUNTRY_CONFIG.
 *
 * Source of truth: `window.location.hostname` (production), with
 * `REACT_APP_DEFAULT_COUNTRY_CODE` env override for preview/staging.
 *
 * Used by:
 *   - CountryContext (auto-detects on mount)
 *   - SignupPage, ProviderPages, HomePage, AboutPage, Footer (display + form labels)
 *   - QuoteRequestPage, QuoteDetailPage (currency display)
 *
 * NOTE: keep in sync with backend `/app/backend/server.py: COUNTRY_CONFIG`
 * so the two sides agree on labels + validation rules.
 */

// ── Locale config (NZ / AU) ─────────────────────────────────────────
export const COUNTRY_CONFIG = {
  NZ: {
    code: 'NZ',
    name: 'New Zealand',
    showMarketplaceActivity: true,
    showProviderDirectory: true,
    flag: '🇳🇿',
    currency: 'NZD',
    currencySymbol: '$',
    currencyPrefix: 'NZ$',
    locale: 'en-NZ',
    gstRate: 0.15,
    gstLabel: 'GST',
    businessNumberLabel: 'NZBN',
    businessNumberPlaceholder: 'e.g. 9429000000000',
    businessNumberHelp: '13-digit New Zealand Business Number',
    phoneCountryCode: '+64',
    phonePlaceholder: '+64 21 xxx xxxx',
    supportEmail: 'support@handyhub.co.nz',
    domain: 'handyhub.co.nz',
    metaPixelId: (process.env.REACT_APP_META_PIXEL_ID_NZ || '1463583158903648'),
    marketingRegion: 'Auckland and NZ-wide',
    teamLabel: 'Auckland-based team',
    hostSuffixes: ['.co.nz', 'handyhub.co.nz'],
    yearlyPrice: 399,
    monthlyPrice: 49,
    yearlySavingsLabel: 'Save NZ$189/year',
    trialDays: 180,
    trialLabelShort: '6-month',
    trialLabelLong: '6 months',
    trialJoinCta: 'Join free for 6 months — no charge today.',
  },
  AU: {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺',
    currency: 'AUD',
    currencySymbol: '$',
    currencyPrefix: 'A$',
    locale: 'en-AU',
    gstRate: 0.10,
    gstLabel: 'GST',
    businessNumberLabel: 'ABN',
    businessNumberPlaceholder: 'e.g. 51 824 753 556',
    businessNumberHelp: '11-digit Australian Business Number',
    phoneCountryCode: '+61',
    phonePlaceholder: '+61 4xx xxx xxx',
    supportEmail: 'support@handyhubau.com.au',
    domain: 'handyhubau.com.au',
    metaPixelId: (process.env.REACT_APP_META_PIXEL_ID_AU || '1463583158903648'),
    marketingRegion: 'across Australia',
    teamLabel: 'Australian-based team',
    hostSuffixes: ['.com.au', 'handyhubau.com.au'],
    yearlyPrice: 499,
    monthlyPrice: 49,
    yearlySavingsLabel: 'Save A$89 per year',
    trialDays: 90,
    trialLabelShort: '90-day',
    // Public marketplace-activity indicators (ratings, reviews, jobs, New/Pending badges)
    // and the browse-all directory are hidden on AU until coverage grows.
    showMarketplaceActivity: false,
    showProviderDirectory: false,
    trialLabelLong: '90 days',
    trialJoinCta: 'Join free for 90 days — no charge today.',
  },
};

// ────────────────────────────────────────────────────────────────────
// Hierarchical location data: State/Region code → City → [suburbs].
// AU expanded (Jun 2026) to broad coverage across all 8 states/territories.
// Suburbs are sorted A-Z at read time by helpers below, so source order
// here does not matter. Existing provider selections are preserved because
// this list is a SUPERSET of the previously-shipped suburbs and matching
// never depends on suburb strings (service/category + country + status only).
// NZ data mirrors the previously-shipped onboarding picker byte-for-byte.
// ────────────────────────────────────────────────────────────────────
export const LOCATIONS_BY_COUNTRY = {
  NZ: {
    AUK: { Auckland: ['CBD', 'Ponsonby', 'Grey Lynn', 'Mt Eden', 'Newmarket', 'Parnell', 'Remuera', 'Epsom', 'Mt Albert', 'Henderson', 'Manukau', 'Takapuna', 'Albany'] },
    WGN: { Wellington: ['CBD', 'Thorndon', 'Lambton Quay', 'Newtown', 'Karori', 'Miramar', 'Kilbirnie', 'Petone', 'Lower Hutt'] },
    CAN: { Christchurch: ['CBD', 'Riccarton', 'Fendalton', 'Merivale', 'Papanui', 'Hornby', 'Linwood'] },
    WKO: { Hamilton: ['CBD', 'Hillcrest', 'Rototuna', 'Te Rapa', 'Chartwell'] },
    BOP: { Tauranga: ['CBD', 'Mt Maunganui', 'Papamoa', 'Greerton', 'Bethlehem'], Rotorua: [] },
    OTA: { Dunedin: ['CBD', 'South Dunedin', 'St Kilda', 'Mosgiel'] },
    HKB: { Napier: [] },
    MWT: { 'Palmerston North': [] },
    NSN: { Nelson: [] },
  },
  AU: {
    NSW: {
      Sydney: ['Auburn', 'Balmain', 'Bankstown', 'Baulkham Hills', 'Blacktown', 'Bondi', 'Bondi Junction', 'Burwood', 'Campbelltown', 'Camperdown', 'Castle Hill', 'CBD', 'Chatswood', 'Coogee', 'Cronulla', 'Darlinghurst', 'Dee Why', 'Double Bay', 'Epping', 'Fairfield', 'Glebe', 'Gordon', 'Hornsby', 'Hurstville', 'Kogarah', 'Lane Cove', 'Leichhardt', 'Liverpool', 'Manly', 'Marrickville', 'Mascot', 'Mosman', 'Newtown', 'North Sydney', 'Paddington', 'Parramatta', 'Penrith', 'Pyrmont', 'Randwick', 'Redfern', 'Rockdale', 'Rouse Hill', 'Ryde', 'Strathfield', 'Surry Hills', 'Sutherland', 'The Rocks', 'Wentworthville', 'Woollahra'],
      Newcastle: ['Adamstown', 'Broadmeadow', 'Charlestown', 'Cooks Hill', 'Hamilton', 'Kotara', 'Lambton', 'Mayfield', 'Merewether', 'New Lambton', 'Newcastle CBD', 'Newcastle East', 'Wallsend'],
      Wollongong: ['Corrimal', 'Dapto', 'Fairy Meadow', 'Figtree', 'Gwynneville', 'Keiraville', 'Port Kembla', 'Shellharbour', 'Thirroul', 'Unanderra', 'Wollongong CBD', 'Woonona'],
      'Central Coast': ['Bateau Bay', 'Erina', 'Gosford', 'Kincumber', 'Long Jetty', 'Terrigal', 'The Entrance', 'Toukley', 'Tuggerah', 'Umina Beach', 'Woy Woy', 'Wyong'],
    },
    VIC: {
      Melbourne: ['Abbotsford', 'Albert Park', 'Balwyn', 'Box Hill', 'Brighton', 'Brunswick', 'Bundoora', 'Camberwell', 'Carlton', 'Caulfield', 'CBD', 'Clayton', 'Coburg', 'Collingwood', 'Craigieburn', 'Cranbourne', 'Dandenong', 'Docklands', 'Doncaster', 'Epping', 'Essendon', 'Fitzroy', 'Footscray', 'Frankston', 'Glen Waverley', 'Hawthorn', 'Kew', 'Malvern', 'Melton', 'Moonee Ponds', 'Mordialloc', 'Northcote', 'Pakenham', 'Port Melbourne', 'Prahran', 'Preston', 'Reservoir', 'Richmond', 'Ringwood', 'South Melbourne', 'South Yarra', 'Southbank', 'St Kilda', 'Sunshine', 'Tarneit', 'Templestowe', 'Thornbury', 'Toorak', 'Werribee', 'Williamstown'],
      Geelong: ['Belmont', 'Corio', 'Geelong CBD', 'Geelong West', 'Grovedale', 'Highton', 'Newtown', 'Norlane', 'Ocean Grove', 'Torquay', 'Waurn Ponds'],
      Ballarat: ['Alfredton', 'Ballarat Central', 'Ballarat East', 'Buninyong', 'Delacombe', 'Sebastopol', 'Wendouree'],
      Bendigo: ['Bendigo CBD', 'Eaglehawk', 'Epsom', 'Golden Square', 'Kangaroo Flat', 'Kennington', 'Strathdale', 'White Hills'],
    },
    QLD: {
      Brisbane: ['Annerley', 'Ascot', 'Aspley', 'Bulimba', 'Camp Hill', 'Cannon Hill', 'Carindale', 'CBD', 'Chermside', 'Clayfield', 'Coorparoo', 'Everton Park', 'Fortitude Valley', 'Graceville', 'Hamilton', 'Holland Park', 'Indooroopilly', 'Kangaroo Point', 'Kelvin Grove', 'Milton', 'Mount Gravatt', 'New Farm', 'Newstead', 'Nundah', 'Paddington', 'Redcliffe', 'Sandgate', 'Sherwood', 'South Bank', 'Spring Hill', 'St Lucia', 'Sunnybank', 'Taringa', 'Toowong', 'West End', 'Wynnum'],
      'Gold Coast': ['Ashmore', 'Benowa', 'Broadbeach', 'Burleigh Heads', 'Coolangatta', 'Coomera', 'Currumbin', 'Helensvale', 'Labrador', 'Main Beach', 'Mermaid Beach', 'Miami', 'Nerang', 'Nobby Beach', 'Palm Beach', 'Robina', 'Southport', 'Surfers Paradise', 'Varsity Lakes'],
      'Sunshine Coast': ['Buderim', 'Caloundra', 'Coolum Beach', 'Maroochydore', 'Mooloolaba', 'Nambour', 'Noosa Heads', 'Peregian Beach', 'Sippy Downs'],
      Cairns: ['Cairns City', 'Cairns North', 'Edge Hill', 'Edmonton', 'Gordonvale', 'Manunda', 'Mooroobool', 'Palm Cove', 'Parramatta Park', 'Redlynch', 'Smithfield', 'Trinity Beach', 'Westcourt', 'Whitfield', 'Woree'],
      Townsville: ['Aitkenvale', 'Annandale', 'Belgian Gardens', 'Douglas', 'Garbutt', 'Hermit Park', 'Kirwan', 'Mundingburra', 'North Ward', 'Pimlico', 'South Townsville', 'Townsville City'],
      Toowoomba: ['Centenary Heights', 'Darling Heights', 'East Toowoomba', 'Harristown', 'Kearneys Spring', 'Newtown', 'Rangeville', 'Toowoomba City', 'Wilsonton'],
    },
    WA: {
      Perth: ['Applecross', 'Armadale', 'Balcatta', 'Bassendean', 'Bayswater', 'Bentley', 'Canning Vale', 'Cannington', 'CBD', 'Claremont', 'Cottesloe', 'Dianella', 'Fremantle', 'Gosnells', 'Joondalup', 'Kalamunda', 'Leederville', 'Midland', 'Mirrabooka', 'Morley', 'Mount Lawley', 'Nedlands', 'Northbridge', 'Osborne Park', 'Rockingham', 'Scarborough', 'Subiaco', 'Success', 'Victoria Park', 'Wanneroo', 'Willetton'],
      Mandurah: ['Dudley Park', 'Falcon', 'Greenfields', 'Halls Head', 'Mandurah CBD', 'Meadow Springs', 'Silver Sands'],
      Bunbury: ['Australind', 'Bunbury CBD', 'Carey Park', 'College Grove', 'East Bunbury', 'Eaton', 'South Bunbury', 'Withers'],
    },
    SA: {
      Adelaide: ['Adelaide CBD', 'Brighton', 'Burnside', 'Campbelltown', 'Elizabeth', 'Findon', 'Gawler', 'Glenelg', 'Golden Grove', 'Henley Beach', 'Hindmarsh', 'Marion', 'Mawson Lakes', 'Mitcham', 'Modbury', 'Morphett Vale', 'North Adelaide', 'Norwood', 'Parafield Gardens', 'Payneham', 'Port Adelaide', 'Prospect', 'Salisbury', 'Semaphore', 'St Peters', 'Tea Tree Gully', 'Unley', 'Walkerville', 'West Lakes'],
    },
    TAS: {
      Hobart: ['Battery Point', 'Bellerive', 'Berriedale', 'CBD', 'Glenorchy', 'Kingston', 'Lenah Valley', 'Moonah', 'New Town', 'North Hobart', 'Rosny', 'Sandy Bay', 'South Hobart', 'West Hobart'],
      Launceston: ['East Launceston', 'Kings Meadows', 'Launceston CBD', 'Mowbray', 'Newnham', 'Newstead', 'Prospect', 'Riverside', 'South Launceston', 'Trevallyn', 'West Launceston'],
    },
    NT: {
      Darwin: ['Casuarina', 'CBD', 'Coconut Grove', 'Fannie Bay', 'Karama', 'Larrakeyah', 'Leanyer', 'Ludmilla', 'Malak', 'Millner', 'Nightcliff', 'Palmerston', 'Parap', 'Rapid Creek', 'Stuart Park'],
      'Alice Springs': ['Araluen', 'Braitling', 'Ciccone', 'Desert Springs', 'East Side', 'Gillen', 'Larapinta', 'Sadadeen', 'The Gap'],
    },
    ACT: {
      Canberra: ['Acton', 'Barton', 'Belconnen', 'Braddon', 'Bruce', 'Civic', 'Deakin', 'Dickson', 'Fyshwick', 'Gungahlin', 'Kambah', 'Kingston', 'Lyneham', 'Manuka', 'Mawson', 'Molonglo', 'Ngunnawal', 'Phillip', 'Tuggeranong', 'Turner', 'Watson', 'Weston', 'Woden', 'Yarralumla'],
    },
  },
};

// ── Cities per state/region (for the hierarchical State → City picker) ──
export const CITIES_BY_STATE = Object.fromEntries(
  Object.entries(LOCATIONS_BY_COUNTRY).map(([cc, states]) => [
    cc,
    Object.fromEntries(
      Object.entries(states).map(([stateCode, cities]) => [
        stateCode,
        Object.keys(cities).sort((a, b) => a.localeCompare(b)),
      ])
    ),
  ])
);

// ── Cities (flat list per country) — derived, backward-compatible ──────
export const CITIES_BY_COUNTRY = Object.fromEntries(
  Object.entries(LOCATIONS_BY_COUNTRY).map(([cc, states]) => [
    cc,
    Array.from(
      new Set(Object.values(states).flatMap((cities) => Object.keys(cities)))
    ).sort((a, b) => a.localeCompare(b)),
  ])
);

// ── Reverse lookup: city name → state code (per country) ───────────────
export const STATE_FOR_CITY = Object.fromEntries(
  Object.entries(LOCATIONS_BY_COUNTRY).map(([cc, states]) => [
    cc,
    Object.fromEntries(
      Object.entries(states).flatMap(([stateCode, cities]) =>
        Object.keys(cities).map((city) => [city, stateCode])
      )
    ),
  ])
);

// ── States/Regions — used when a more granular picker than city is needed ──
export const STATES_BY_COUNTRY = {
  NZ: [
    { code: 'AUK', name: 'Auckland' },
    { code: 'WGN', name: 'Wellington' },
    { code: 'CAN', name: 'Canterbury' },
    { code: 'WKO', name: 'Waikato' },
    { code: 'BOP', name: 'Bay of Plenty' },
    { code: 'OTA', name: 'Otago' },
    { code: 'MWT', name: 'Manawatū-Whanganui' },
    { code: 'HKB', name: "Hawke's Bay" },
    { code: 'NTL', name: 'Northland' },
    { code: 'TKI', name: 'Taranaki' },
    { code: 'STL', name: 'Southland' },
    { code: 'TAS', name: 'Tasman' },
    { code: 'NSN', name: 'Nelson' },
    { code: 'MBH', name: 'Marlborough' },
    { code: 'WTC', name: 'West Coast' },
    { code: 'GIS', name: 'Gisborne' },
  ],
  AU: [
    { code: 'VIC', name: 'Victoria' },
    { code: 'NSW', name: 'New South Wales' },
    { code: 'QLD', name: 'Queensland' },
    { code: 'SA',  name: 'South Australia' },
    { code: 'WA',  name: 'Western Australia' },
    { code: 'TAS', name: 'Tasmania' },
    { code: 'NT',  name: 'Northern Territory' },
    { code: 'ACT', name: 'Australian Capital Territory' },
  ],
};

// ── Suburbs per city (derived from LOCATIONS_BY_COUNTRY, A-Z sorted) ──
// A city name resolves to its suburb list regardless of country. City names
// are unique across the two markets so a flat map is safe and keeps every
// existing consumer (suburbsForCity(city)) working unchanged.
export const SUBURBS_BY_CITY = (() => {
  const out = {};
  for (const states of Object.values(LOCATIONS_BY_COUNTRY)) {
    for (const cities of Object.values(states)) {
      for (const [city, suburbs] of Object.entries(cities)) {
        const existing = out[city] || [];
        out[city] = Array.from(new Set([...existing, ...suburbs])).sort((a, b) =>
          a.localeCompare(b)
        );
      }
    }
  }
  return out;
})();

// ── Location helpers ────────────────────────────────────────────────
/** Cities within a state/region, A-Z. */
export const getCitiesForState = (countryCode, stateCode) => {
  const cc = (countryCode || 'NZ').toUpperCase();
  return (CITIES_BY_STATE[cc] && CITIES_BY_STATE[cc][stateCode]) || [];
};

/** Suburbs for a city, A-Z. */
export const getSuburbsForCity = (city) => SUBURBS_BY_CITY[city] || [];

/** Reverse lookup: which state/region a city belongs to. */
export const getStateForCity = (countryCode, city) => {
  const cc = (countryCode || 'NZ').toUpperCase();
  return (STATE_FOR_CITY[cc] && STATE_FOR_CITY[cc][city]) || '';
};

/** Case-insensitive partial suburb search within a city. */
export const searchSuburbs = (city, query) => {
  const all = getSuburbsForCity(city);
  const q = (query || '').trim().toLowerCase();
  if (!q) return all;
  return all.filter((s) => s.toLowerCase().includes(q));
};

// Flat "Suburb, City" options for AU (A-Z). Used by the homepage location
// autocomplete. "Suburb, City" form naturally disambiguates duplicate suburb
// names (e.g. "CBD, Sydney" vs "CBD, Melbourne") and carries city context
// forward into the quote-request flow.
export const AU_SUBURB_OPTIONS = (() => {
  const out = [];
  for (const cities of Object.values(LOCATIONS_BY_COUNTRY.AU)) {
    for (const [city, subs] of Object.entries(cities)) {
      for (const s of subs) out.push(`${s}, ${city}`);
    }
  }
  return Array.from(new Set(out)).sort((a, b) => a.localeCompare(b));
})();

// ── Auto-detection from hostname ────────────────────────────────────
export const resolveCountryFromHost = (host) => {
  if (!host) return null;
  const h = String(host).toLowerCase().split(':')[0];
  // Match longer / more specific suffixes first to handle edge cases
  for (const [code, cfg] of Object.entries(COUNTRY_CONFIG)) {
    for (const suffix of cfg.hostSuffixes) {
      if (h === suffix || h.endsWith(suffix)) return code;
    }
  }
  return null;
};

/**
 * Country resolution order:
 *   1. Explicit override via REACT_APP_DEFAULT_COUNTRY_CODE env var
 *   2. window.location.hostname (production source of truth)
 *   3. localStorage (user toggle in dev/preview)
 *   4. 'NZ' fallback
 */
export const detectCountry = () => {
  const envOverride = (process.env.REACT_APP_DEFAULT_COUNTRY_CODE || '').toUpperCase().trim();
  if (envOverride && COUNTRY_CONFIG[envOverride]) return envOverride;

  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const fromHost = resolveCountryFromHost(window.location.hostname);
    if (fromHost) return fromHost;
  }

  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = window.localStorage.getItem('handyhub_country');
    if (stored && COUNTRY_CONFIG[stored]) return stored;
  }

  return 'NZ';
};

export const getCountryConfig = (code) => {
  const upper = (code || 'NZ').toUpperCase();
  return COUNTRY_CONFIG[upper] || COUNTRY_CONFIG.NZ;
};

// ── Formatting helpers ──────────────────────────────────────────────
export const formatCurrency = (amount, code) => {
  if (amount == null || isNaN(amount)) return '';
  const cfg = getCountryConfig(code);
  try {
    return new Intl.NumberFormat(cfg.locale, {
      style: 'currency',
      currency: cfg.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${cfg.currencyPrefix}${Number(amount).toFixed(2)}`;
  }
};

// ── Business-number validation (mirrors backend) ────────────────────
/** Australian Business Number — 11 digits, weighted modulo-89 algorithm. */
export const validateABN = (abn) => {
  if (!abn) return false;
  const digits = String(abn).replace(/\D/g, '');
  if (digits.length !== 11) return false;
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const nums = digits.split('').map(d => parseInt(d, 10));
  nums[0] -= 1;
  const total = nums.reduce((sum, n, i) => sum + n * weights[i], 0);
  return total % 89 === 0;
};

/** NZ Business Number — 13 digits (no checksum). */
export const validateNZBN = (nzbn) => {
  if (!nzbn) return false;
  const digits = String(nzbn).replace(/\D/g, '');
  return digits.length === 13;
};

export const validateBusinessNumber = (value, countryCode) => {
  const code = (countryCode || '').toUpperCase();
  if (code === 'AU') return validateABN(value);
  if (code === 'NZ') return validateNZBN(value);
  return Boolean(value && String(value).trim());
};
